import React, { useRef, useState } from 'react'
import { FlatList, StyleProp, StyleSheet, TouchableOpacity, TouchableWithoutFeedback, View, ViewStyle } from 'react-native'
import { AppModal as Modal } from '../atoms/AppModal'
import { useTheme } from '@react-navigation/native'
import { Icon, Text } from '../atoms'
import { Theme } from '@theme'

type DropdownItem = { id: string; name: string }

interface ContextualDropdownProps {
  data: readonly DropdownItem[]
  /** Text shown in the trigger button */
  label: string
  /** Id of the currently selected item (shows checkmark + bold) */
  selectedId?: string
  onSelect: (id: string) => void
  /** Style applied to the trigger button container */
  buttonStyle?: StyleProp<ViewStyle>
}

export const ContextualDropdown = ({ data, label, selectedId, onSelect, buttonStyle }: ContextualDropdownProps) => {
  const theme = useTheme()
  const styles = getStyle(theme)
  const [isOpen, setIsOpen] = useState(false)
  const [menuLayout, setMenuLayout] = useState<{ x: number; y: number; width: number } | null>(null)
  const buttonRef = useRef<TouchableOpacity>(null)

  const handleOpen = () => {
    buttonRef.current?.measure((_x, _y, width, height, pageX, pageY) => {
      setMenuLayout({ x: pageX, y: pageY + height, width })
      setIsOpen(true)
    })
  }

  const handleSelect = (id: string) => {
    setIsOpen(false)
    onSelect(id)
  }

  return (
    <>
      <TouchableOpacity
        ref={buttonRef}
        style={[styles.button, buttonStyle, isOpen && styles.buttonOpen]}
        onPress={handleOpen}
      >
        <Text type="bodySmall" style={styles.label}>{label}</Text>
        <Icon name={isOpen ? 'arrowUp' : 'arrowDown'} size={theme.iconSize.medium} color={theme.colors.black} />
      </TouchableOpacity>

      <Modal transparent visible={isOpen} animationType="fade" onRequestClose={() => setIsOpen(false)}>
        <TouchableWithoutFeedback onPress={() => setIsOpen(false)}>
          <View style={styles.backdrop}>
            {menuLayout && (
              <View style={[styles.menu, { top: menuLayout.y, left: menuLayout.x, minWidth: menuLayout.width }]}>
                <FlatList
                  data={data}
                  keyExtractor={(item) => item.id}
                  scrollEnabled={false}
                  renderItem={({ item, index }) => (
                    <TouchableOpacity
                      style={[styles.menuItem, index === data.length - 1 && styles.menuItemLast]}
                      onPress={() => handleSelect(item.id)}
                    >
                      <Text type={selectedId === item.id ? 'bodySmallBold' : 'bodySmall'}>{item.name}</Text>
                      {selectedId === item.id && (
                        <Icon name="check" size={theme.iconSize.small} color={theme.colors.black} />
                      )}
                    </TouchableOpacity>
                  )}
                />
              </View>
            )}
          </View>
        </TouchableWithoutFeedback>
      </Modal>
    </>
  )
}

const getStyle = (theme: Theme) => StyleSheet.create({
  button: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderWidth: 1,
    borderColor: theme.colors.lightGrey,
    paddingVertical: theme.spacing.sm,
    paddingHorizontal: theme.spacing.lg,
    borderRadius: theme.spacing.xl,
  },
  buttonOpen: {
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },
  // Grows in a full-width button, but keeps its own width in a compact one
  // (`flex: 1` means a zero base width: the label vanished on native).
  label: {
    flexGrow: 1,
    flexShrink: 1,
  },
  backdrop: {
    flex: 1,
  },
  menu: {
    position: 'absolute',
    backgroundColor: theme.colors.white,
    borderRadius: theme.spacing.sm,
    borderTopRightRadius: 0,
    borderTopLeftRadius: 0,
    borderWidth: 1,
    borderTopWidth: 0,
    borderColor: theme.colors.lightGrey,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.12,
    shadowRadius: 8,
    elevation: 8,
    overflow: 'hidden',
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: theme.spacing.sm,
    paddingHorizontal: theme.spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.lightGrey,
  },
  menuItemLast: {
    borderBottomWidth: 0,
  },
})
